import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axiosClient, { axiosClientAdmin } from '../lib/auth';
import { endShift } from '@/lib/api/shifts/shift';
import { toast } from '@/hooks/use-toast';
import { TenantDomain, TenantId, TokenAdmin, TokenMonitoring } from './util';
import { RedeemTokenResponse } from '@/lib/api/auth/auth';
import { useAlertMetrics } from '@/hooks/useAlertMetrics';
import { trackShiftEnd } from '@/app/Shift/util/trackShiftStartEnd';

export type AdminUserDto = {  
  tokenExpiry?: Date;
  token?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  number?: string;
  smsAuthorised?: boolean;
  roles?: string[];
  lockedOut?: boolean;
  userName?: string;
  tenantId?: string;
  availableTenants?: TenantDetails[];
}

export type ProjectAccessDto = {
  id?: string;
  name?: string;
}

export type CompanyAccessDto = {
  id?: string;
  name?: string;
  projects?: ProjectAccessDto[];
  tenantId?: string;
}

export type LoggedInUserResponse = {
  success?: boolean;
  message?: string;
  code?: number;
  errors?: string[];
  user?: AdminUserDto;
  companies?: CompanyAccessDto[];
}

export type TenantDetails = {
  tenantId: string;
  tenantName: string;
  themePath: string;
  themeVersion: number;
  roles: string[];
}

export type ResetPasswordResponse = {
  success?: boolean;
  message?: string;
  code?: number;
  errors?: string[];
  user?: AdminUserDto;
}

export type TokenResetForm = {
  token?: string;
  password?: string;
  userId?: string;
}

export type ShiftCreated = {
  success: boolean;
  shift: ShiftDto;
}

export type ShiftEnded = {
  success: boolean;
}

export type ShiftDto = {
  id: string;
  start: string;
  lastSeen: string;
  end: string | null;
}

export type CurrentShiftFetched = {
  success?: boolean;
  message?: string;
  code?: string;
  errors?: string[];
  shift?: ShiftDto;
  requiresShift?: boolean;
}

type AuthState = {
  user: { id: string; username: string; firstName: string; lastName: string; email: string } | null;
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  isAuthenticated: boolean;
  isAuthenticatedAdmin: boolean;
  isAuthenticatedMonitor: boolean;
  isCustomerUser: boolean;
  error: string | null;
  tokenExpiry: Date | null;
  loginOneLink: (userData: AdminUserDto) => void;
  determineTenant: (
    opts: { email: string; password: string },
    promptForTenant: (tenants: TenantDetails[]) => Promise<TenantDetails | null>
  ) => Promise<string | null>;
  login: (userData: { userName: string; password: string }) => Promise<string | null>;
  loginAdmin: (userData: { userName: string; password: string }) => Promise<string | null>;
  loginToken: () => Promise<void>;
  logout: (redirect?: boolean) => Promise<void>;
  resetPassword: (token: string) => void;
  checkTokenExpiry: () => void;
  companyName: string | null;
  setInitialized: (value: boolean) => void;
  getTenantData: () => TenantDetails | null;
  loggingOut: boolean;
  initialized: boolean;
  tenant: TenantDetails | null;
}

// Helper functions
const hasMessage = (value: unknown): value is { message: string } =>
  typeof value === 'object' &&
  value !== null &&
  'message' in value &&
  typeof value.message === 'string';

// Helper to create user object from AdminUserDto
const createUserFromDto = (user: AdminUserDto) => ({
  id: user.id!,
  username: user.userName!,
  firstName: user.firstName!,
  lastName: user.lastName!,
  email: user.email!,
});

// Helper to set authenticated state
const setAuthenticatedState = (
  set: (state: Partial<AuthState>) => void,
  user: AdminUserDto,
  isAdmin: boolean,
  isMonitor: boolean
) => {
  set({
    user: createUserFromDto(user),
    roles: user.roles ?? [],
    isAuthenticatedAdmin: isAdmin,
    isAuthenticatedMonitor: isMonitor,
    isAuthenticated: true,
    isCustomerUser: false,
    error: null,
  });

  if (user.tokenExpiry) {
    set({ tokenExpiry: user.tokenExpiry });
  }
};

const blankState = {
  user: null,
  id: '',
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  roles: [],
  isAuthenticated: false,
  isAuthenticatedAdmin: false,
  isAuthenticatedMonitor: false,
  isCustomerUser: false,
  error: null,
  tokenExpiry: null,
  companyName: null,
  initialized: false,
  loggingOut: false,
  onBreak: false,
  tenant: null,
}

// Helper to clear all auth state
const clearAuthState = (): Partial<AuthState> => ({
  ...blankState
});


export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // When adding a new property, make sure to either deal with the default value on an
      // previously authed user, or just increment the version to clear
      ...blankState,

      // TODO Not currently used
      checkTokenExpiry: () => {
        const state = useAuthStore.getState();
        if (state.tokenExpiry && new Date(state.tokenExpiry) < new Date()) {
          state.logout();
          window.location.href = '/login';
        }
      },
      determineTenant: async (opts, promptForTenant) => {
        try {
          const response = await axiosClientAdmin.get<{
            tenants: {
              id: string;
              name: string;
              themePath: string;
              themeVersion: number;
              urlBase: string;
            }[];
          }>('Auth/tenants', {
            auth: {
              password: encodeURIComponent(opts.password),
              username: encodeURIComponent(opts.email),
            },
          });

          const availableTenants = response.data.tenants.map((tenant) => ({
            tenantId: tenant.id,
            tenantName: tenant.name,
            themePath: tenant.themePath,
            themeVersion: tenant.themeVersion,
            roles: [],
          }));

          if (availableTenants.length === 0) {
            throw new Error('Could not get tenants');
          }

          let selectedTenant: TenantDetails;
          if (availableTenants.length === 1) {
            selectedTenant = availableTenants[0];
          } else {
            const userSelectedTenant = await promptForTenant(availableTenants);
            if (!userSelectedTenant) {
              return 'No tenant selected';
            }
            selectedTenant = userSelectedTenant;
          }

          TenantId.set(selectedTenant.tenantId);
          set({ tenant: selectedTenant });
          
          return null;
        } catch (ex) {
          if (hasMessage(ex)) {
            return ex.message;
          }
          return 'An unknown error occurred';
        }
      },

      login: async (userData: { userName: string; password: string }) => {
        const { isAuthenticated } = get();
        
        // End shift if user is currently authenticated
        if (isAuthenticated) {
          await get().logout(false);
        }

        try {
          const response = await axiosClient.post<LoggedInUserResponse>('/Auth/monitoring/Login', {
            email: userData.userName.trim(),
            password: userData.password.trim(),
          });

          if (response.data.user?.token) {
            TokenMonitoring.set(response.data.user.token);
            setAuthenticatedState(set, response.data.user, false, true);
            return null;
          }
          return 'Invalid username or password';
        } catch {
          return 'Invalid username or password';
        }
      },

      loginOneLink: async (userData: AdminUserDto) => {
        const { isAuthenticated } = get();
        if (isAuthenticated) {
          await get().logout(false);
        }

        
        if (userData.token) {
          TokenAdmin.set(userData.token);
        }

        if (userData.tokenExpiry) {
          set({ tokenExpiry: userData.tokenExpiry });
        }

        set({
          user: createUserFromDto(userData),
          roles: userData.roles ?? [],
          isAuthenticatedMonitor: true,
          isAuthenticated: true,
          isCustomerUser: false,
          error: null,
        });

      },
      loginAdmin: async (userData: {
        userName: string;
        password: string;
      }): Promise<string | null> => {
        const { isAuthenticatedAdmin } = get();
        if (isAuthenticatedAdmin) {
          await get().logout(false);
        }

        try {
          const response = await axiosClientAdmin.post<LoggedInUserResponse>('/Auth/Login', {
            email: userData.userName.trim(),
            password: userData.password.trim(),
          });
          if (response.data.user?.token) {
            TokenAdmin.set(response.data.user.token);

            setAuthenticatedState(set, response.data.user, true, get().isAuthenticatedMonitor);
            return null;
          }
          return 'Failed to login to admin';
        } catch (error: unknown) {
          console.log('Admin Error', error);
          if (hasMessage(error)) {
            return error.message;
          }
          return 'Failed to login to admin';
        }
      },

      loginToken: async () => {
        // Check to make sure that there is a token in the URL
        const searchParams = new URLSearchParams(window.location.search);
        if (!searchParams.has('token')) {
          return;
        }

        const { isAuthenticated, logout } = get();
        if (isAuthenticated) {
          await logout(false);
        }

        try {
          const loginRequest = await axiosClient.post<{ user: AdminUserDto }>(
            'Auth/monitoring/tokenlogin',
            JSON.stringify({
              token: searchParams.get('token'),
            }),
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          const { user } = loginRequest.data;

          if (user.tokenExpiry) {
            set({
              tokenExpiry: user.tokenExpiry,
            });
          }

          set({
            isAuthenticatedMonitor: true,
            isAuthenticated: true,
            isCustomerUser: false,
            user: createUserFromDto(user),
            roles: user.roles ?? [],
          });

          if (user.token) {
            TokenMonitoring.set(user.token);
          }
        } catch (ex) {
          console.error('Invalid auth token ', ex);
        }

        searchParams.delete('token');
        window.location.search = searchParams.toString();
      },

      logout: async (redirect = false) => {
        const { isAuthenticated, loggingOut, user } = get();
        
        // Prevent re-entrant logout calls
        if (loggingOut) {
          return;
        }
        
        set({ loggingOut: true });
        
        // End shift if user is authenticated
        // Wrapped in try-catch to prevent 401 errors (e.g., from stale credentials
        // when switching APIs) from interrupting the logout flow
        if (isAuthenticated) {
          try {
            await endShift();
          } catch {
            // Ignore errors - we're logging out anyway
          }
          trackShiftEnd(user);
        }
        
        delete axiosClient.defaults.headers.common.Authorization;
        delete axiosClientAdmin.defaults.headers.common.Authorization;

        set(clearAuthState());

        TokenAdmin.delete();
        TokenMonitoring.delete();
        TenantId.delete();
        TenantDomain.delete();
        useAuthStore.persist.clearStorage();
        
        if (redirect) {
          window.location.href = '/login';
        }
      },

      resetPassword: async (token) => {
        try {
          await axiosClient.post<ResetPasswordResponse>('/Auth/DoTokenReset/' + token, {
            //todo
          });
        } catch (error: unknown) {
          set({
            error: hasMessage(error) ? error.message : 'Failed to reset password',
          });
        }
      },
      setInitialized: (value: boolean) => {
        set({ initialized: value });
      },
      getTenantData: () => {
        return get().tenant;
      },
    }),
    {
      name: 'auth-storage',
      // Increment the version to clear the current store, and force a login.
      version: 4,
      partialize: (state) => {
        // Exclude loggingOut from persistence - it's transient UI state that
        // should always start as false on page load. If persisted, a refresh
        // during logout would leave the user stuck.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { loggingOut, ...rest } = state;
        return rest;
      },
      onRehydrateStorage: () => (state) => {
        if (!state?.isAuthenticated) return;

        const token = TokenMonitoring.get()

        if (token) {
          axiosClient.defaults.headers.common.Authorization = `Bearer ${token}`;
        }

        const adminToken = TokenAdmin.get()
        if (adminToken) {
          axiosClientAdmin.defaults.headers.common.Authorization = `Bearer ${adminToken}`;
        }

        state.setInitialized(true);
      },
    }
  )
);

export const loginWithRedeemToken = (
  { tenant, user, adminToken, monitoringToken }: RedeemTokenResponse
) => {
  const hasAdmin = adminToken != null;
  const hasMonitoring = monitoringToken != null;

  if (!hasAdmin && !hasMonitoring) {
    toast({
      title: 'Error getting login tokens. Please contact an administrator',
    });
    return;
  }

  if (hasAdmin) TokenAdmin.set(adminToken);
  if (hasMonitoring) TokenMonitoring.set(monitoringToken);

  const state: Partial<AuthState> = {};

  if (user) {
    state.user = {
      id: user.id!,
      username: user.userName!,
      firstName: user.firstName!,
      lastName: user.lastName!,
      email: user.email!,
    };
    state.roles = user.roles ?? [];
    state.isAuthenticated = true;
    state.isAuthenticatedAdmin = hasAdmin;
    state.isAuthenticatedMonitor = hasMonitoring;
    state.isCustomerUser = false;
    state.error = null;
  }

  if (tenant.tenantId) {
    state.tenant = {
      tenantName: tenant.tenantName ?? '',
      roles: tenant.roles ?? [],
      tenantId: tenant.tenantId ?? '',
      themePath: tenant.themePath ?? '',
      themeVersion: tenant.themeVersion ?? 0,
    };

    TenantId.set(tenant.tenantId);
  }

  useAuthStore.setState(state);
};
